import type { VenueStateLike } from "./session.ts";
import { servedClosure } from "./faultEvidence.ts";

export interface OcvPoint {
  t: number;
  o: number;
  c: number;
  v: number | null;
}

export interface MoneyFlowPoint { t: number; value: number }
export interface MoneyFlowDomain { min: number; max: number }

export function candleStudyBlocked(
  root: string | null,
  servedRung: string | null,
  requestedRung: string,
  snapshotArrived = true,
): string | null {
  if (!snapshotArrived) return null;
  if (root === null) return "candle root UNREPORTED — refusing to label rootless bars as ES";
  if (root !== "ES") return `candle route served root=${root}, expected ES — refusing to pool roots`;
  if (servedRung === null) return `candle rung UNREPORTED (requested ${requestedRung}) — refusing an assumed match`;
  if (servedRung !== requestedRung) {
    return `candle route served rung ${servedRung}, requested ${requestedRung} — refusing to mislabel studies`;
  }
  return null;
}

export interface RangeLevelsState {
  venueClosed: VenueStateLike | null;
  showError: boolean;
  logError: boolean;
}

/** One decision feeds the range pill, visible refusal, and log arm. */
export function rangeLevelsState(
  error: string | null,
  servedVenueState: VenueStateLike | null | undefined,
): RangeLevelsState {
  const closed = servedClosure(servedVenueState) !== null;
  return {
    venueClosed: closed ? servedVenueState ?? null : null,
    showError: error !== null,
    logError: error !== null,
  };
}

export function moneyFlowPoints(candles: readonly OcvPoint[]): MoneyFlowPoint[] {
  return candles.flatMap((c) => c.v === null || !Number.isFinite(c.v)
    ? []
    : [{ t: c.t, value: (c.c - c.o) * c.v }]);
}

export function moneyFlowDomain(points: readonly MoneyFlowPoint[]): MoneyFlowDomain {
  const extent = Math.max(1, ...points.map((point) => Math.abs(point.value)));
  return { min: -extent, max: extent };
}

export function moneyFlowY(
  value: number,
  domain: MoneyFlowDomain,
  height: number,
  padding: number,
): number {
  return height - padding - ((value - domain.min) / (domain.max - domain.min)) * (height - padding * 2);
}

export function shouldSelfFetchStudies(feed: unknown, price: unknown): boolean {
  return feed === undefined && price === undefined;
}

/** Withhold the divergence join whenever the candle identity was not admitted. */
export function priceObservationsForCvd<T>(price: T[], candleBlocked: string | null): T[] | null {
  return candleBlocked === null ? price : null;
}
